import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, ArrowRight, Loader2, Check, Sparkles, Share2, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLang } from "@/i18n/LanguageProvider";

const API = process.env.REACT_APP_BACKEND_URL;

function formatRate(rate) {
  if (!rate) return "—";
  return rate < 10 ? rate.toFixed(4) : rate.toFixed(2);
}

function humanDate(iso, lang) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(lang === "fr" ? "fr-FR" : "en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch (_) {
    return iso;
  }
}

/**
 * RateLockCard — 24h "lock this rate" signup incentive.
 *
 * Used on CorridorDetail.jsx. Posts to /api/rate-locks and shows the locked
 * rate, expiry, and a CTA to finish signup on /waitlist.
 */
export default function RateLockCard({ slug, fromCcy, toCcy, rate }) {
  const { t } = useTranslation();
  const { lang, prefix } = useLang();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | ok | already | error
  const [errMsg, setErrMsg] = useState("");
  const [lock, setLock] = useState(null);
  const [stats, setStats] = useState(null);

  // Poll the 24h leaderboard so the counter feels alive without a websocket.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API}/api/rate-locks-stats`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setStats(d);
      } catch (_) { /* ignore */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [status]);

  // If a lock is already stored client-side, restore it so refreshes keep the
  // successful state.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`ix.rate-lock.${slug}`);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.expires_at && new Date(parsed.expires_at) > new Date()) {
        setLock(parsed);
        setStatus("ok");
      } else {
        localStorage.removeItem(`ix.rate-lock.${slug}`);
      }
    } catch (_) { /* ignore */ }
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    setErrMsg("");
    try {
      const r = await fetch(`${API}/api/rate-locks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, slug }),
      });
      const d = await r.json();
      if (!r.ok) {
        setStatus("error");
        setErrMsg(d?.detail || t("rateLock.errorRetry"));
        return;
      }
      setLock(d);
      setStatus(d.already_locked ? "already" : "ok");
      try {
        localStorage.setItem(`ix.rate-lock.${slug}`, JSON.stringify(d));
      } catch (_) { /* ignore */ }
    } catch (_) {
      setStatus("error");
      setErrMsg(t("rateLock.errorNet"));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
      className="mt-16 rounded-3xl bg-canvas border border-forest/12 p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-6 items-center relative overflow-hidden"
      data-testid="rate-lock"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(600px 220px at 90% 15%, rgba(232,255,0,0.35), transparent 60%), radial-gradient(500px 200px at 10% 90%, rgba(74,29,150,0.10), transparent 60%)",
        }}
      />

      <div className="md:col-span-7 relative">
        <div className="inline-flex items-center gap-2 label-eyebrow">
          <Lock className="w-4 h-4 text-forest" strokeWidth={2} />
          {t("rateLock.eyebrow")}
        </div>
        <h3 className="mt-3 font-display font-medium text-forest text-3xl md:text-4xl tracking-tightest leading-tight">
          {t("rateLock.headline")}{" "}
          <span className="italic font-serif text-forest/85">
            {t("rateLock.headlineItalic")}
          </span>{" "}
          {t("rateLock.headlineTail")}
        </h3>
        <p className="mt-4 text-ink/70 max-w-lg leading-relaxed">
          {t("rateLock.body")}
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-forest text-canvas px-4 py-2 text-sm font-mono">
            <Sparkles className="w-4 h-4 text-lime" strokeWidth={2} />
            1 {fromCcy} = {formatRate(rate)} {toCcy}
          </span>
          {stats && stats.total_24h > 0 && (
            <span
              className="inline-flex items-center gap-2 rounded-full border border-forest/20 bg-canvas px-3 py-1.5 text-[11px] font-semibold text-forest"
              data-testid="rate-lock-leaderboard"
            >
              <span className="live-dot" aria-hidden />
              {t("rateLock.leaderboard", { count: stats.total_24h })}
            </span>
          )}
        </div>
      </div>

      <div className="md:col-span-5 relative">
        {status === "ok" || status === "already" ? (
          <div
            className="rounded-2xl bg-forest text-canvas p-5"
            data-testid="rate-lock-success"
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-8 h-8 rounded-full bg-lime text-forest grid place-items-center">
                <Check className="w-4 h-4" strokeWidth={2.5} />
              </div>
              <div className="min-w-0">
                <div className="font-display text-lg tracking-tight text-lime">
                  {status === "already"
                    ? t("rateLock.alreadyTitle")
                    : t("rateLock.successTitle")}
                </div>
                <div className="mt-1 text-[11px] tracking-[0.22em] uppercase text-canvas/60 font-bold">
                  {t("rateLock.lockedAt")}
                </div>
                <div className="mt-1 font-display text-2xl md:text-3xl tracking-tightest">
                  1 {lock?.from_ccy} = {formatRate(lock?.rate)} {lock?.to_ccy}
                </div>
                <div className="mt-2 text-xs text-canvas/70">
                  {t("rateLock.goodUntil")}{" "}
                  <span className="font-mono text-lime">
                    {humanDate(lock?.expires_at, lang)}
                  </span>
                </div>
              </div>
            </div>
            <Link
              to={prefix(`/waitlist?source=rate-lock-${slug}`)}
              className="mt-5 inline-flex items-center gap-2 bg-lime text-ink px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-lime/90 transition-colors"
              data-testid="rate-lock-finish"
            >
              {t("rateLock.finishSignup")} <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
            {lock?.token && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="rate-lock-share-row">
                <button
                  type="button"
                  onClick={async () => {
                    const shareUrl = `${API}/api/og/lock/${lock.token}`;
                    if (navigator.share) {
                      try {
                        await navigator.share({
                          title: "I locked a rate on Instantxfer CashApp",
                          text: `Locked 1 ${lock.from_ccy} = ${lock.rate.toFixed(2)} ${lock.to_ccy} for 24h.`,
                          url: shareUrl,
                        });
                      } catch (_) { /* cancelled */ }
                    } else {
                      try { await navigator.clipboard.writeText(shareUrl); } catch (_) { /* ignore */ }
                    }
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-lime/40 text-lime hover:bg-lime/10 text-xs font-semibold transition-colors"
                  data-testid="rate-lock-share"
                >
                  <Share2 className="w-3.5 h-3.5" strokeWidth={2} />
                  {t("rateLock.share")}
                </button>
                <a
                  href={`${API}/api/og/lock/${lock.token}`}
                  download={`instantxfer-lock-${slug}.png`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-canvas/25 text-canvas hover:bg-canvas/10 text-xs font-semibold transition-colors"
                  data-testid="rate-lock-download"
                >
                  <Download className="w-3.5 h-3.5" strokeWidth={2} />
                  {t("rateLock.downloadCard")}
                </a>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-3" data-testid="rate-lock-form">
            <label className="block">
              <span className="text-[10px] tracking-[0.22em] uppercase text-ink/50 font-semibold">
                Email
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-1.5 w-full rounded-xl border border-forest/15 bg-canvas text-ink placeholder:text-ink/40 px-4 py-3 focus:outline-none focus:border-forest transition-colors"
                data-testid="rate-lock-email"
              />
            </label>
            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex items-center justify-center gap-2 bg-forest text-canvas px-5 py-3 rounded-full text-sm font-semibold hover:bg-forest/90 transition-colors disabled:opacity-60"
              data-testid="rate-lock-submit"
            >
              {status === "loading" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                  {t("rateLock.submitting")}
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" strokeWidth={2} />
                  {t("rateLock.submit")}
                </>
              )}
            </button>
            {status === "error" && (
              <div className="text-xs text-red-600" data-testid="rate-lock-error">
                {errMsg}
              </div>
            )}
          </form>
        )}
      </div>
    </motion.div>
  );
}
